// Sekai Tensei translations (EN / FR / ES)
export const SUPPORTED_LANGS = ["en", "fr", "es"];

export const detectBrowserLang = () => {
  if (typeof navigator === "undefined") return "en";
  const langs = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || "en"]).map((l) => l.toLowerCase());
  for (const l of langs) {
    if (l.startsWith("fr")) return "fr";
    if (l.startsWith("es")) return "es";
    if (l.startsWith("en")) return "en";
  }
  return "en";
};

export const translations = {
  en: {
    nav: { shop: "SHOP", lang: "LANG" },
    hero: {
      romaji: "SEKAI TENSEI",
      tagline: "A SHOOT 'EM UP CARTRIDGE FOR AMSTRAD GX4000 & CPC6128+",
      preorder: "PRE-ORDER NOW",
      scroll: "INSERT COIN ▾",
    },
    video: {
      title: "TRANSMISSION // PROMO",
      caption: "Press PLAY to receive the broadcast",
    },
    story: {
      title: "THE STORY",
      year: "YEAR 5039 — LOCATION: THE MULTIVERSE",
      targetTitle: "TARGET DATA",
      targetId: "LEVEL BOSS",
      targetAlias: "UNBEATABLE",
      targetThreat: "MAX",
      targetStatus: "AT LARGE",
      targetIdLabel: "ID",
      targetAliasLabel: "ALIAS",
      targetThreatLabel: "THREAT",
      targetStatusLabel: "STATUS",
      paragraphs: [
        "Across the nebulae, seven alternate versions of planet Earth have been attacked: seven Earths, each following a different fate. All have been corrupted, deformed, altered by the same dark will — that of KUROTENSHI, the Black Angel, the Devastator of Planets, who spreads desolation from one world to the next.",
        "On the first Earth, swarms of spores fly above frozen ground. On the second, lava flows drown ancient cities. Further out, an ocean has swallowed everything; dead forests suffocate; machines have taken power — and at the deepest end of the Multiverse, Kurotenshi waits in his own sanctuary.",
        "You are NOZORA, the last living sentinel. You were sent here inside a protective shell — the MODULE — nearly indestructible, the only way to traverse the corridors of space-time. Each jump is exhausting; you must reclaim your power after every leap from one Earth to another. To do so, you must absorb the energy of battle, break the cocoon, and reveal your true nature.",
        "Seven planets. Seven bosses. Seven versions of humanity to snatch back from oblivion.",
        "Good luck, NOZORA — the Multiverse is counting on you.",
      ],
    },
    forms: {
      title: "TWO FORMS // ONE DESTINY",
      module: {
        name: "THE MODULE",
        desc: "At the dawn of each level, NOZORA reaches the planet inside The MODULE. It does not fly — it levitates above the ground, drags its targeting reticle, aims at enemies and fires. It offers better protection thanks to its own life bar, but its firepower stays modest and its mobility is almost nil. Collect five transformation orbs to trigger the humanoid awakening.",
      },
      nozora: {
        name: "NOZORA",
        desc: "Once five transformation orbs are absorbed, the MODULE releases its host. NOZORA appears: nimble, the humanoid heroine flies and unleashes streams of pure energy. Her firepower exceeds the MODULE's — but without its shell she is fragile: one single hit and she falls.",
      },
    },
    planets: {
      title: "SEVEN PLANETS",
      subtitle: "7 levels of increasing difficulty across the Multiverse.",
      items: [
        { n: "01", name: "ICE EARTH", desc: "A perpetual wind sweeps over plains of blue snow. Comets break from the black sky and the ground creaks under the glacial mass." },
        { n: "02", name: "VOLCANIC EARTH", desc: "The sky is blood-red. Magma bubbles drift above burning flows and the ground cracks open." },
        { n: "03", name: "OCEAN EARTH", desc: "The whole planet is one stretch of water. You glide above bioluminescent reefs in a turquoise sky crossed by silent lightning." },
        { n: "04", name: "DESERT EARTH", desc: "Sand to the horizon, blinding sun. Death has reigned here for ages — defenses are scarce." },
        { n: "05", name: "MECHANICAL EARTH", desc: "The planet has been replaced by its own machinery: towers, conduits, giant processors. Flying enemies are legion." },
        { n: "06", name: "DEAD FOREST EARTH", desc: "A whole forest has dried, still standing. Violet spores fall from the dead trees and a pale wind shakes leafless branches." },
        { n: "07", name: "KUROTENSHI", desc: "The sanctuary of the Black Angel. The ground is obsidian, the sky lead. This is where you will finally meet the Devastator of Planets." },
      ],
    },
    controls: {
      title: "CONTROLS",
      table: [
        { btn: "↑ ↓ ← →", action: "MODULE: move reticle / NOZORA: move character" },
        { btn: "Button A / 1 / Space", action: "FIRE" },
        { btn: "Button B / 2 / W or Z", action: "BOMB (2 per life)" },
        { btn: "Pause (II) / P", action: "PAUSE" },
      ],
      bombNote: "The bomb is a screen-wide attack: it flashes the border and destroys everything that is not a boss. Save one for tight moments — especially in HARD mode.",
    },
    specs: {
      title: "SYSTEM REQUIREMENTS",
      lines: [
        "> HARDWARE......: AMSTRAD GX4000 / AMSTRAD CPC6128+",
        "> FORMAT........: CARTRIDGE — 256 KB",
        "> CONTROLLER....: JOYSTICK OR 2-BUTTON PAD",
        "> PLAYERS.......: 1",
        "> LANGUAGES.....: EN / FR / ES",
        "> READY ▮",
      ],
    },
    cartridge: {
      title: "THE CARTRIDGE",
      desc: "Sekai Tensei ships as a physical GX4000-format cartridge — plug it in, power on, blast off.",
    },
    footer: {
      credits: "Developed by Tugs · Music by ТАРОСТI · SFX by Tugs · Nozora Pixel Art by alonerone",
      copy: "© 2026 TUGS — ALL RIGHTS RESERVED",
    },
  },

  fr: {
    nav: { shop: "BOUTIQUE", lang: "LANGUE" },
    hero: {
      romaji: "SEKAI TENSEI",
      tagline: "UN SHOOT'EM UP CARTOUCHE POUR AMSTRAD GX4000 & CPC6128+",
      preorder: "PRÉCOMMANDER",
      scroll: "INSÉRER PIÈCE ▾",
    },
    video: {
      title: "TRANSMISSION // PROMO",
      caption: "Appuie sur PLAY pour recevoir la diffusion",
    },
    story: {
      title: "L'HISTOIRE",
      year: "ANNÉE 5039 — LIEU : LE MULTIVERS",
      targetTitle: "DONNÉES CIBLE",
      targetId: "BOSS DU NIVEAU",
      targetAlias: "IMBATTABLE",
      targetThreat: "MAX",
      targetStatus: "EN LIBERTÉ",
      targetIdLabel: "ID",
      targetAliasLabel: "ALIAS",
      targetThreatLabel: "MENACE",
      targetStatusLabel: "STATUT",
      paragraphs: [
        "À travers les nébuleuses, sept versions alternatives de la planète Terre ont été attaquées : sept Terres, chacune suivant un destin différent. Toutes ont été corrompues, déformées, altérées par la même volonté noire — celle de KUROTENSHI, l'Ange Noir, le Dévastateur de Planètes, qui sème la désolation d'un monde à l'autre.",
        "Sur la première Terre, des nuées de spores volent au-dessus du sol gelé. Sur la seconde, des coulées de lave noient les anciennes cités. Plus loin, l'océan a tout englouti, des forêts mortes asphyxiées, les machines ont pris le pouvoir — et tout au fond du Multivers, Kurotenshi attend, dans son propre sanctuaire.",
        "Tu es NOZORA, la dernière sentinelle vivante. Tu as été envoyée ici sous une forme de protection — le MODULE — presque indestructible, le seul moyen de traverser les couloirs de l'espace-temps. Chaque voyage est épuisant, et tu dois retrouver ton pouvoir après un saut d'une Terre à l'autre : pour cela, il faudra absorber l'énergie du combat, briser le cocon, et révéler ta vraie nature.",
        "Sept planètes. Sept boss. Sept versions de l'humanité à arracher à l'oubli.",
        "Bonne chance, NOZORA — le Multivers compte sur toi.",
      ],
    },
    forms: {
      title: "DEUX FORMES // UN DESTIN",
      module: {
        name: "LE MODULE",
        desc: "À l'aube de chaque niveau, NOZORA se rend sur la planète avec Le MODULE. Le MODULE ne vole pas : il lévite au-dessus du sol, déplace sa cible, vise les ennemis et tire. Il offre une meilleure protection grâce à sa propre barre de vie, mais sa puissance de feu reste modeste et sa mobilité quasi nulle. Collecte cinq orbes de transformation pour déclencher l'éveil de l'humanoïde.",
      },
      nozora: {
        name: "NOZORA",
        desc: "Une fois les 5 orbes de transformation absorbées, le MODULE libère son hôte. NOZORA apparaît : plus agile, l'héroïne humanoïde vole et tire des flux d'énergie pure. Sa puissance de feu est supérieure à celle du MODULE, mais elle est plus vulnérable sans cette protection : un seul coup suffit pour qu'elle tombe.",
      },
    },
    planets: {
      title: "SEPT PLANÈTES",
      subtitle: "7 niveaux à difficulté croissante à travers le Multivers.",
      items: [
        { n: "01", name: "TERRE DE GLACE", desc: "Un vent perpétuel souffle sur des plaines de neige bleue. Des comètes se détachent du ciel noir et le sol crisse sous la masse glaciaire." },
        { n: "02", name: "TERRE VOLCANIQUE", desc: "Le ciel est rouge sang. Des bulles de magma flottent au-dessus de coulées brûlantes et le sol se craquelle." },
        { n: "03", name: "TERRE OCÉAN", desc: "Toute la planète n'est plus qu'une étendue d'eau. Tu planes au-dessus de récifs bioluminescents, dans un ciel turquoise traversé d'éclairs silencieux." },
        { n: "04", name: "TERRE DÉSERT", desc: "Sable jusqu'à l'horizon, soleil aveuglant. La mort règne ici depuis longtemps et les défenses qui restent sont rares." },
        { n: "05", name: "TERRE MÉCANIQUE", desc: "La planète a été remplacée par sa propre machinerie : tours, conduits, processeurs gigantesques. Les ennemis volants y sont légion." },
        { n: "06", name: "TERRE DE LA FORÊT MORTE", desc: "Une forêt entière a séché, debout. Des spores violettes tombent des arbres morts et un vent pâle agite des branches sans feuilles." },
        { n: "07", name: "KUROTENSHI", desc: "Le sanctuaire de l'Ange Noir. Le sol est d'obsidienne, le ciel de plomb. C'est là que tu le trouveras enfin, le Dévastateur des Planètes." },
      ],
    },
    controls: {
      title: "COMMANDES",
      table: [
        { btn: "↑ ↓ ← →", action: "MODULE : déplacer la cible / NOZORA : déplacer le personnage" },
        { btn: "Bouton A / 1 / Espace", action: "TIR" },
        { btn: "Bouton B / 2 / W ou Z", action: "BOMBE (2 par vie)" },
        { btn: "Pause (II) / P", action: "PAUSE" },
      ],
      bombNote: "La bombe est une attaque à effet de zone : elle balaie l'écran, fait flasher la bordure et détruit tout ce qui n'est pas un boss. Garde-en une pour les moments serrés — surtout en mode HARD.",
    },
    specs: {
      title: "CONFIGURATION REQUISE",
      lines: [
        "> MACHINE.......: AMSTRAD GX4000 / AMSTRAD CPC6128+",
        "> FORMAT........: CARTOUCHE — 256 KO",
        "> CONTRÔLEUR....: JOYSTICK OU PAD 2 BOUTONS",
        "> JOUEURS......: 1",
        "> LANGUES......: FR / EN / ES",
        "> PRÊT ▮",
      ],
    },
    cartridge: {
      title: "LA CARTOUCHE",
      desc: "Sekai Tensei est édité au format cartouche physique GX4000 — insère, allume, décolle.",
    },
    footer: {
      credits: "Développé par Tugs · Musiques par ТАРОСТI · SFX par Tugs · Pixel Art Nozora par alonerone",
      copy: "© 2026 TUGS — TOUS DROITS RÉSERVÉS",
    },
  },

  es: {
    nav: { shop: "TIENDA", lang: "IDIOMA" },
    hero: {
      romaji: "SEKAI TENSEI",
      tagline: "UN SHOOT'EM UP EN CARTUCHO PARA AMSTRAD GX4000 & CPC6128+",
      preorder: "RESERVAR AHORA",
      scroll: "INSERTAR MONEDA ▾",
    },
    video: {
      title: "TRANSMISIÓN // PROMO",
      caption: "Pulsa PLAY para recibir la emisión",
    },
    story: {
      title: "LA HISTORIA",
      year: "AÑO 5039 — LUGAR: EL MULTIVERSO",
      targetTitle: "DATOS OBJETIVO",
      targetId: "JEFE DEL NIVEL",
      targetAlias: "IMBATIBLE",
      targetThreat: "MÁX",
      targetStatus: "EN LIBERTAD",
      targetIdLabel: "ID",
      targetAliasLabel: "ALIAS",
      targetThreatLabel: "AMENAZA",
      targetStatusLabel: "ESTADO",
      paragraphs: [
        "A través de las nebulosas, siete versiones alternativas del planeta Tierra han sido atacadas: siete Tierras, cada una con un destino distinto. Todas han sido corrompidas, deformadas, alteradas por la misma voluntad oscura — la de KUROTENSHI, el Ángel Negro, el Devastador de Planetas, que siembra la desolación de un mundo a otro.",
        "En la primera Tierra, enjambres de esporas vuelan sobre el suelo helado. En la segunda, coladas de lava ahogan ciudades antiguas. Más lejos, el océano lo ha engullido todo; bosques muertos asfixiados; las máquinas han tomado el poder — y en lo más profundo del Multiverso, Kurotenshi aguarda en su propio santuario.",
        "Tú eres NOZORA, la última centinela viva. Fuiste enviada aquí dentro de una protección — el MÓDULO — casi indestructible, la única manera de atravesar los corredores del espacio-tiempo. Cada salto es agotador y debes recuperar tu poder tras pasar de una Tierra a otra: para ello, deberás absorber la energía del combate, romper el capullo y revelar tu verdadera naturaleza.",
        "Siete planetas. Siete jefes. Siete versiones de la humanidad que arrancar del olvido.",
        "Buena suerte, NOZORA — el Multiverso cuenta contigo.",
      ],
    },
    forms: {
      title: "DOS FORMAS // UN DESTINO",
      module: {
        name: "EL MÓDULO",
        desc: "Al inicio de cada nivel, NOZORA llega al planeta dentro del MÓDULO. El MÓDULO no vuela: levita sobre el suelo, desplaza su mira, apunta a los enemigos y dispara. Ofrece mejor protección gracias a su propia barra de vida, pero su potencia de fuego es modesta y su movilidad casi nula. Recoge cinco orbes de transformación para desencadenar el despertar humanoide.",
      },
      nozora: {
        name: "NOZORA",
        desc: "Una vez absorbidas las 5 orbes de transformación, el MÓDULO libera a su anfitriona. NOZORA aparece: ágil, la heroína humanoide vuela y dispara flujos de energía pura. Su potencia de fuego supera la del MÓDULO, pero sin esa protección es frágil: un solo impacto y cae.",
      },
    },
    planets: {
      title: "SIETE PLANETAS",
      subtitle: "7 niveles de dificultad creciente a través del Multiverso.",
      items: [
        { n: "01", name: "TIERRA DE HIELO", desc: "Un viento perpetuo barre llanuras de nieve azul. Cometas se desprenden del cielo negro y el suelo cruje bajo la masa glacial." },
        { n: "02", name: "TIERRA VOLCÁNICA", desc: "El cielo es rojo sangre. Burbujas de magma flotan sobre coladas ardientes y el suelo se agrieta." },
        { n: "03", name: "TIERRA OCÉANO", desc: "Todo el planeta no es más que una extensión de agua. Te deslizas sobre arrecifes bioluminiscentes en un cielo turquesa cruzado por relámpagos silenciosos." },
        { n: "04", name: "TIERRA DESIERTO", desc: "Arena hasta el horizonte, sol cegador. La muerte reina aquí desde hace mucho y las defensas son escasas." },
        { n: "05", name: "TIERRA MECÁNICA", desc: "El planeta ha sido sustituido por su propia maquinaria: torres, conductos, procesadores gigantes. Los enemigos voladores son legión." },
        { n: "06", name: "TIERRA DEL BOSQUE MUERTO", desc: "Un bosque entero se ha secado en pie. Esporas violetas caen de los árboles muertos y un viento pálido agita las ramas sin hojas." },
        { n: "07", name: "KUROTENSHI", desc: "El santuario del Ángel Negro. El suelo es obsidiana, el cielo plomo. Aquí encontrarás por fin al Devastador de Planetas." },
      ],
    },
    controls: {
      title: "CONTROLES",
      table: [
        { btn: "↑ ↓ ← →", action: "MÓDULO: mover la mira / NOZORA: mover al personaje" },
        { btn: "Botón A / 1 / Espacio", action: "DISPARO" },
        { btn: "Botón B / 2 / W o Z", action: "BOMBA (2 por vida)" },
        { btn: "Pausa (II) / P", action: "PAUSA" },
      ],
      bombNote: "La bomba es un ataque de área: barre la pantalla, hace parpadear el borde y destruye todo lo que no sea un jefe. Guarda una para los momentos críticos — sobre todo en modo HARD.",
    },
    specs: {
      title: "REQUISITOS DEL SISTEMA",
      lines: [
        "> MÁQUINA.......: AMSTRAD GX4000 / AMSTRAD CPC6128+",
        "> FORMATO.......: CARTUCHO — 256 KB",
        "> CONTROLADOR..: JOYSTICK O PAD DE 2 BOTONES",
        "> JUGADORES....: 1",
        "> IDIOMAS......: ES / EN / FR",
        "> LISTO ▮",
      ],
    },
    cartridge: {
      title: "EL CARTUCHO",
      desc: "Sekai Tensei se edita en cartucho físico formato GX4000 — inserta, enciende, despega.",
    },
    footer: {
      credits: "Desarrollado por Tugs · Música por ТАРОСТI · SFX por Tugs · Pixel Art de Nozora por alonerone",
      copy: "© 2026 TUGS — TODOS LOS DERECHOS RESERVADOS",
    },
  },
};
